/**
 * AnalyzeTool - Content Analysis Interface
 *
 * Provides real analysis of workspace content via NPE backends:
 * - AI Detection (pure stats, no LLM)
 * - SIC Analysis (LLM-based)
 * - Quantum Sentencing (LLM + embeddings)
 *
 * Results are read-only and do not modify the workspace buffer.
 *
 * @module @humanizer/studio/components/tools/AnalyzeTool
 */

import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { useAnalyzeApi } from '../../hooks/useAnalyzeApi';
import type {
  DetectionResult, DetectionFeatures, SicResult, SentenceStepResult, TetralemmaReading,
  GPTZeroDetectionResult, SicProgressUpdate,
  SentencingV2Result, SentencingV2Sentence, SentenceRole, DiagnosticFlag,
  NoemaGraphResult, NoemaGraphDiffResult, NoemaProgressUpdate,
  ChapterGraphResult, ChapterGraphProgress,
} from '../../types/analysis';
import { NoemaGraphView } from './NoemaGraphView';
import { NoemaGraphDiffView } from './NoemaGraphDiffView';
import { ChapterGraphView } from './ChapterGraphView';
import { useBook } from '../../contexts/BookContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface AnalyzeToolProps {
  className?: string;
  onSentencingComplete?: (steps: SentenceStepResult['step'][]) => void;
  onRoleClassifierComplete?: (data: SentencingV2Result | null) => void;
  onNoemaGraphComplete?: (data: NoemaGraphResult | null) => void;
  onSicComplete?: (data: SicResult | null) => void;
  onChapterGraphComplete?: (data: ChapterGraphResult | null) => void;
}

interface AnalysisOption {
  id: 'ai-detection' | 'sic' | 'sentencing' | 'sentencing-v2' | 'noema-graph' | 'noema-diff' | 'chapter-graph';
  label: string;
  icon: string;
  description: string;
  needsLlm: boolean;
}

type AnalysisStatus = 'idle' | 'running' | 'error';

type AnalysisResult =
  | { type: 'detection'; data: DetectionResult; gptzeroData?: GPTZeroDetectionResult }
  | { type: 'sic'; data: SicResult }
  | { type: 'sentencing'; data: SentencingData }
  | { type: 'sentencing-v2'; data: SentencingV2Result }
  | { type: 'noema-graph'; data: NoemaGraphResult }
  | { type: 'noema-diff'; data: NoemaGraphDiffResult }
  | { type: 'chapter-graph'; data: ChapterGraphResult };

interface SentencingData {
  steps: SentenceStepResult['step'][];
  totalSentences: number;
  progress: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const ANALYSIS_OPTIONS: AnalysisOption[] = [
  {
    id: 'ai-detection',
    label: 'AI Detection',
    icon: '\u{1F916}',
    description: 'Statistical AI pattern detection - instant, no LLM required',
    needsLlm: false,
  },
  {
    id: 'sic',
    label: 'SIC Analysis',
    icon: '\u{1F4D0}',
    description: 'Subjective Intentional Constraint analysis with LLM judge',
    needsLlm: true,
  },
  {
    id: 'sentencing',
    label: 'Sentencing',
    icon: '\u2696\uFE0F',
    description: 'Quantum sentence-by-sentence reading analysis with tetralemma',
    needsLlm: true,
  },
  {
    id: 'sentencing-v2',
    label: 'Role Classifier',
    icon: '\u{1F3AF}',
    description: 'Structural role analysis: anchors, pivots, scaffolding, redundancy detection',
    needsLlm: false,
  },
  {
    id: 'noema-graph',
    label: 'Noema Graph',
    icon: '\u{1F9E0}',
    description: 'Phenomenological analysis: eidetic objects and noetic acts in your text',
    needsLlm: true,
  },
  {
    id: 'noema-diff',
    label: 'Graph Diff',
    icon: '\u{1F504}',
    description: 'Compare two texts\u2019 intentional graphs: track objects lost, added, and stance shifts',
    needsLlm: true,
  },
  {
    id: 'chapter-graph',
    label: 'Chapter Graph',
    icon: '\u{1F4D6}',
    description: 'Cross-chapter object threading: through-lines, transitions, tension/novelty arcs across your book',
    needsLlm: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// RESULT RENDERERS
// ═══════════════════════════════════════════════════════════════════════════

const FEATURE_GROUPS: { label: string; keys: (keyof DetectionFeatures)[] }[] = [
  { label: 'Lexical', keys: ['ttrWindowed', 'hapaxLegomenaRate', 'contractionDensity', 'ngramDiversity'] },
  { label: 'Structural', keys: ['burstiness', 'sentenceComplexity', 'sentenceLengthKurtosis'] },
  { label: 'Content', keys: ['tellPhraseScore', 'punctuationSignals', 'punctuationContrast', 'compressionRatio'] },
  { label: 'Embedding', keys: ['embeddingTrajectory'] },
];

const FEATURE_LABELS: Record<string, string> = {
  ttrWindowed: 'Vocabulary Richness (TTR)',
  hapaxLegomenaRate: 'Hapax Legomena Rate',
  contractionDensity: 'Contraction Density',
  ngramDiversity: 'N-gram Diversity',
  burstiness: 'Burstiness (CV)',
  sentenceComplexity: 'Sentence Complexity',
  sentenceLengthKurtosis: 'Sentence Length Kurtosis',
  tellPhraseScore: 'Tell Phrase Score',
  punctuationSignals: 'Punctuation Signals',
  punctuationContrast: 'Punctuation Contrast',
  compressionRatio: 'Compression Ratio',
  embeddingTrajectory: 'Trajectory Smoothness',
  semicolonRate: 'Semicolon Rate',
  emDashRate: 'Em-dash Rate',
};

function DetectionResultView({ data }: { data: DetectionResult }) {
  const verdictClass = `analyze-tool__verdict--${data.verdict}`;

  return (
    <div className="analyze-tool__detection">
      <div className="analyze-tool__score-row">
        <span className="analyze-tool__score-label">AI Likelihood</span>
        <span className="analyze-tool__score-value">{data.aiLikelihood != null ? (data.aiLikelihood > 1 ? data.aiLikelihood.toFixed(1) : (data.aiLikelihood * 100).toFixed(1)) : '\u2014'}%</span>
      </div>

      <div className={`analyze-tool__verdict-badge ${verdictClass}`}>
        {data.verdict.toUpperCase()} ({data.confidence} confidence)
      </div>

      <div className="analyze-tool__features">
        {FEATURE_GROUPS.map(group => {
          const entries = group.keys
            .map(key => ({ key, value: data.features[key] }))
            .filter(e => e.value != null);
          if (entries.length === 0) return null;
          return (
            <div key={group.label} className="analyze-tool__feature-group">
              <div className="analyze-tool__features-header">{group.label}</div>
              {entries.map(({ key, value }) => (
                <div key={key} className="analyze-tool__feature-row">
                  <span className="analyze-tool__feature-name">{FEATURE_LABELS[key] || key}</span>
                  <span className="analyze-tool__feature-value">
                    {typeof value === 'number' ? value.toFixed(3) : String(value)}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {data.embeddingTrajectory && (
        <div className="analyze-tool__trajectory">
          <div className="analyze-tool__features-header">Embedding Trajectory</div>
          <div className="analyze-tool__feature-row">
            <span className="analyze-tool__feature-name">Mean Similarity</span>
            <span className="analyze-tool__feature-value">{data.embeddingTrajectory.meanSimilarity.toFixed(3)}</span>
          </div>
          <div className="analyze-tool__feature-row">
            <span className="analyze-tool__feature-name">Similarity Variance</span>
            <span className="analyze-tool__feature-value">{data.embeddingTrajectory.similarityVariance.toFixed(4)}</span>
          </div>
          <div className="analyze-tool__feature-row">
            <span className="analyze-tool__feature-name">Semantic Jumps</span>
            <span className="analyze-tool__feature-value">{data.embeddingTrajectory.semanticJumps} / {data.embeddingTrajectory.pairCount}</span>
          </div>
        </div>
      )}

      {data.tellPhrases.matches.length > 0 && (
        <div className="analyze-tool__tell-phrases">
          <div className="analyze-tool__features-header">
            Tell Phrases ({data.tellPhrases.matches.length} found)
          </div>
          {data.tellPhrases.matches.map((m, i) => (
            <div key={i} className="analyze-tool__tell-phrase">
              <span className="analyze-tool__tell-phrase-name">{m.phrase}</span>
              <span className="analyze-tool__tell-phrase-meta">
                {m.category} | {m.count}x | weight: {m.weight.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      )}

      {data.humanizationRecommendations.length > 0 && (
        <div className="analyze-tool__recommendations">
          <div className="analyze-tool__features-header">Recommendations</div>
          {data.humanizationRecommendations.map((rec, i) => (
            <div key={i} className={`analyze-tool__rec analyze-tool__rec--${rec.priority}`}>
              <span className="analyze-tool__rec-priority">[{rec.priority}]</span>
              {rec.description}
            </div>
          ))}
        </div>
      )}

      {data.sentenceAnalysis && data.sentenceAnalysis.length > 0 && (
        <div className="analyze-tool__sentences">
          <div className="analyze-tool__features-header">
            Per-Sentence Analysis ({data.sentenceAnalysis.length} sentences)
          </div>
          {data.sentenceAnalysis.map((s, i) => (
            <div
              key={i}
              className={`analyze-tool__sentence ${s.isSuspect ? 'analyze-tool__sentence--suspect' : ''}`}
            >
              <div className="analyze-tool__sentence-score">
                {(s.aiLikelihood > 1 ? s.aiLikelihood : s.aiLikelihood * 100).toFixed(0)}%
              </div>
              <div className="analyze-tool__sentence-text">{s.text}</div>
            </div>
          ))}
        </div>
      )}

      <div className="analyze-tool__meta">
        {data.processingTimeMs}ms | {data.detectorVersion} | {data.method}
      </div>
    </div>
  );
}

const SIC_FEATURE_LABELS: Record<string, string> = {
  commitment_irreversibility: 'Commitment / Irreversibility',
  epistemic_risk_uncertainty: 'Epistemic Risk / Uncertainty',
  time_pressure_tradeoffs: 'Time Pressure / Tradeoffs',
  situatedness_body_social: 'Situatedness (Body/Social)',
  scar_tissue_specificity: 'Scar Tissue Specificity',
  bounded_viewpoint: 'Bounded Viewpoint',
  anti_smoothing: 'Anti-Smoothing (higher = better)',
  meta_contamination: 'Meta-Contamination (lower = better)',
};

const SIC_FEATURE_TOOLTIPS: Record<string, string> = {
  commitment_irreversibility: 'Concrete decisions with consequences that narrow future options',
  epistemic_risk_uncertainty: 'Being wrong, surprises, actual stakes of not knowing',
  time_pressure_tradeoffs: 'Deadlines, urgency, asymmetric time awareness',
  situatedness_body_social: 'Physical presence, social exposure, reputation at stake',
  scar_tissue_specificity: 'Involuntary reactions with temporal persistence',
  bounded_viewpoint: 'Non-omniscient narration, acknowledges limits of knowledge',
  anti_smoothing: 'Refusal of false symmetry (higher = more committed)',
  meta_contamination: 'Manager voice, meta-exposition (lower = better)',
};

function SicScoreBar({ score, isNegative }: { score: number; isNegative?: boolean }) {
  const pct = Math.min(100, Math.max(0, score));
  const cls = isNegative
    ? (pct > 50 ? 'analyze-tool__sic-bar--warn' : 'analyze-tool__sic-bar--good')
    : (pct > 60 ? 'analyze-tool__sic-bar--good' : pct > 30 ? 'analyze-tool__sic-bar--mid' : 'analyze-tool__sic-bar--low');
  return (
    <div className="analyze-tool__sic-bar-track">
      <div className={`analyze-tool__sic-bar-fill ${cls}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function SicResultView({ data }: { data: SicResult }) {
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);

  return (
    <div className="analyze-tool__sic">
      <div className="analyze-tool__score-row">
        <span className="analyze-tool__score-label">SIC Score</span>
        <span className="analyze-tool__score-value">{data.sicScore ?? '—'}</span>
      </div>
      <div className="analyze-tool__score-row">
        <span className="analyze-tool__score-label">Genre</span>
        <span className="analyze-tool__score-value">{data.genre}</span>
      </div>

      {Object.keys(data.features).length > 0 && (
        <div className="analyze-tool__features">
          <div className="analyze-tool__features-header">Constraint Features (0-100)</div>
          {Object.entries(data.features).map(([key, val]) => {
            const isNegative = key === 'meta_contamination';
            const isExpanded = expandedFeature === key;
            return (
              <div key={key} className="analyze-tool__sic-feature">
                <button
                  className="analyze-tool__sic-feature-header"
                  onClick={() => setExpandedFeature(isExpanded ? null : key)}
                  aria-expanded={isExpanded}
                  title={SIC_FEATURE_TOOLTIPS[key]}
                >
                  <span className="analyze-tool__feature-name-group">
                    <span className="analyze-tool__feature-name">
                      {SIC_FEATURE_LABELS[key] || key.replace(/_/g, ' ')}
                    </span>
                    {SIC_FEATURE_TOOLTIPS[key] && (
                      <span className="analyze-tool__feature-tooltip">{SIC_FEATURE_TOOLTIPS[key]}</span>
                    )}
                  </span>
                  <span className="analyze-tool__feature-value">{val.score}</span>
                </button>
                <SicScoreBar score={val.score} isNegative={isNegative} />
                {isExpanded && (
                  <div className="analyze-tool__sic-feature-detail">
                    {val.notes && <div className="analyze-tool__sic-notes">{val.notes}</div>}
                    {val.evidence.length > 0 && (
                      <div className="analyze-tool__sic-evidence">
                        {val.evidence.map((e, i) => (
                          <div key={i} className="analyze-tool__sic-quote">
                            <q>{e.quote}</q>
                            {e.rationale && <span className="analyze-tool__sic-rationale">{e.rationale}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data.inflectionPoints.length > 0 && (
        <div className="analyze-tool__inflections">
          <div className="analyze-tool__features-header">Inflection Points</div>
          {data.inflectionPoints.map((ip, i) => (
            <div key={i} className="analyze-tool__inflection">
              <span className="analyze-tool__inflection-type">{ip.kind}</span>
              <q className="analyze-tool__inflection-text">{ip.quote}</q>
              {ip.whyItMatters && (
                <span className="analyze-tool__inflection-why">{ip.whyItMatters}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {data.notes && (
        <div className="analyze-tool__notes">{data.notes}</div>
      )}

      <div className="analyze-tool__meta">
        {data.processingTimeMs ? `${data.processingTimeMs}ms` : ''}
        {data.llmCallCount ? ` | ${data.llmCallCount} LLM calls` : ''}
        {' | sic.v1'}
      </div>
    </div>
  );
}

const TETRA_CORNERS = ['literal', 'metaphorical', 'both', 'neither'] as const;

const TETRA_LABELS: Record<string, string> = {
  literal: 'literal',
  metaphorical: 'metaphor',
  both: 'both',
  neither: 'neither',
};

function getDominantCorner(reading: TetralemmaReading): typeof TETRA_CORNERS[number] {
  let best: typeof TETRA_CORNERS[number] = 'literal';
  let bestProb = 0;
  for (const c of TETRA_CORNERS) {
    if (reading[c].probability > bestProb) {
      bestProb = reading[c].probability;
      best = c;
    }
  }
  return best;
}

function SentencingResultView({ data }: { data: SentencingData }) {
  const isComplete = data.progress >= 1;

  return (
    <div className="analyze-tool__sentencing">
      {!isComplete && (
        <div className="analyze-tool__progress">
          <div className="analyze-tool__progress-bar">
            <div
              className="analyze-tool__progress-fill"
              style={{ width: `${data.progress * 100}%` }}
            />
          </div>
          <span className="analyze-tool__progress-label">
            {data.steps.length} / {data.totalSentences} sentences
          </span>
        </div>
      )}

      {isComplete && (
        <div className="analyze-tool__applied-msg">
          Analysis applied to workspace
        </div>
      )}

      {data.steps.filter((s): s is NonNullable<typeof s> => !!s).map((step, i) => {
        const reading = step.measurement?.reading;
        const dominant = reading ? getDominantCorner(reading) : null;

        return (
          <div key={i} className="analyze-tool__step">
            <div className="analyze-tool__step-sentence">{step.sentence}</div>
            {reading && (
              <div className="analyze-tool__step-pills">
                {TETRA_CORNERS.map((corner) => (
                  <span
                    key={corner}
                    className={`analyze-tool__tetra-pill analyze-tool__tetra-pill--${corner} ${corner === dominant ? 'analyze-tool__tetra-pill--dominant' : ''}`}
                  >
                    {TETRA_LABELS[corner]} {(reading[corner].probability * 100).toFixed(0)}%
                  </span>
                ))}
              </div>
            )}
            {step.measurement && (
              <div className="analyze-tool__step-meta">
                rho: {step.rhoDistance?.toFixed(3) ?? '—'}
                {!step.measurement.isValid && ' | invalid'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GPTZeroSentenceRow({ sentence, index }: { sentence: GPTZeroDetectionResult['details']['sentences'][0]; index: number }) {
  const [expanded, setExpanded] = useState(false);

  let sentenceClass = 'analyze-tool__gptzero-sentence--human';
  if (sentence.highlight_sentence_for_ai || sentence.generated_prob > 0.7) {
    sentenceClass = 'analyze-tool__gptzero-sentence--ai';
  } else if (sentence.generated_prob > 0.3) {
    sentenceClass = 'analyze-tool__gptzero-sentence--mixed';
  }

  const label = sentence.generated_prob > 0.7 ? 'AI-generated'
    : sentence.generated_prob > 0.3 ? 'Uncertain' : 'Human-written';

  return (
    <div
      className={`analyze-tool__gptzero-sentence ${sentenceClass} ${expanded ? 'analyze-tool__gptzero-sentence--expanded' : ''}`}
      onClick={() => setExpanded(v => !v)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); } }}
      title={`AI: ${(sentence.generated_prob * 100).toFixed(1)}% | Paraphrased: ${(sentence.paraphrased_prob * 100).toFixed(1)}%${sentence.highlight_sentence_for_ai ? ' | Flagged by GPTZero' : ''}`}
      aria-expanded={expanded}
      aria-label={`Sentence ${index + 1}: ${label}, ${(sentence.generated_prob * 100).toFixed(0)}% AI probability`}
    >
      <div className="analyze-tool__gptzero-sentence-prob">
        {(sentence.generated_prob * 100).toFixed(0)}%
      </div>
      <div className="analyze-tool__gptzero-sentence-text">
        {sentence.sentence}
        {sentence.highlight_sentence_for_ai && (
          <span className="analyze-tool__gptzero-flag" aria-label="Flagged by GPTZero">*</span>
        )}
      </div>
      {expanded && (
        <div className="analyze-tool__gptzero-sentence-detail">
          <span>AI: {(sentence.generated_prob * 100).toFixed(1)}%</span>
          <span>Paraphrase: {(sentence.paraphrased_prob * 100).toFixed(1)}%</span>
          {sentence.highlight_sentence_for_ai && <span className="analyze-tool__gptzero-detail-flag">Flagged</span>}
        </div>
      )}
    </div>
  );
}

function GPTZeroResultView({ data }: { data: GPTZeroDetectionResult }) {
  const verdictClass = `analyze-tool__verdict--${data.verdict}`;

  return (
    <div className="analyze-tool__gptzero">
      <div className="analyze-tool__gptzero-header">
        <span className="analyze-tool__gptzero-label">GPTZero Neural Detection</span>
      </div>

      <div className="analyze-tool__score-row">
        <span className="analyze-tool__score-label">AI Confidence</span>
        <span className="analyze-tool__score-value">{data.confidence.toFixed(1)}%</span>
      </div>

      <div className={`analyze-tool__verdict-badge ${verdictClass}`}>
        {data.verdict.toUpperCase()} ({data.confidence_category})
      </div>

      {data.subclass_type && data.subclass_type !== 'unknown' && (
        <div className="analyze-tool__gptzero-subclass">
          {data.subclass_type === 'pure_ai' ? 'Pure AI' : 'AI Paraphrased'}
        </div>
      )}

      {data.result_message && (
        <div className="analyze-tool__gptzero-message">{data.result_message}</div>
      )}

      {data.details.sentences.length > 0 && (
        <div className="analyze-tool__gptzero-sentences">
          <div className="analyze-tool__features-header">
            Per-Sentence Analysis ({data.details.sentences.length} sentences)
          </div>
          <div className="analyze-tool__gptzero-sentence-hint">
            Tap a sentence for details
          </div>
          {data.details.sentences.map((s, i) => (
            <GPTZeroSentenceRow key={i} sentence={s} index={i} />
          ))}
        </div>
      )}

      {data.quotaUsed != null && data.quotaLimit != null && (
        <div className="analyze-tool__gptzero-quota">
          Cloud quota: {data.quotaUsed}/{data.quotaLimit} this month
        </div>
      )}

      <div className="analyze-tool__meta">
        {data.processingTimeMs}ms | {data.classVersion || 'v2'} | gptzero
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SENTENCING V2 RESULT VIEW
// ═══════════════════════════════════════════════════════════════════════════

const ROLE_LABELS: Record<SentenceRole, string> = {
  anchor: 'ANCHOR', advance: 'ADVANCE', ground: 'GROUND',
  pivot: 'PIVOT', echo: 'ECHO', bridge: 'BRIDGE', scaffold: 'SCAFF',
};

const ROLE_ORDER: SentenceRole[] = ['anchor', 'advance', 'ground', 'pivot', 'echo', 'bridge', 'scaffold'];

function SentencingV2ResultView({ data }: { data: SentencingV2Result }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showSection, setShowSection] = useState<'sentences' | 'redundancy' | 'gaps'>('sentences');

  const totalSentences = data.totalSentences || 1;

  return (
    <div className="sentencing-v2">
      {/* Summary stats */}
      <div className="sentencing-v2__summary">
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Sentences</span>
          <span className="sentencing-v2__stat-value">{data.totalSentences}</span>
        </div>
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Chapters</span>
          <span className="sentencing-v2__stat-value">{data.totalChapters}</span>
        </div>
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Redundant</span>
          <span className="sentencing-v2__stat-value">
            {data.redundancyClusters.reduce((sum, c) => sum + c.sentences.length, 0)}
          </span>
        </div>
        <div className="sentencing-v2__stat">
          <span className="sentencing-v2__stat-label">Gaps</span>
          <span className="sentencing-v2__stat-value">{data.coherenceGaps.length}</span>
        </div>
      </div>

      {/* Role distribution bar */}
      <div className="sentencing-v2__role-bar">
        {ROLE_ORDER.map(role => {
          const count = data.roleDistribution[role] || 0;
          if (count === 0) return null;
          return (
            <div
              key={role}
              className={`sentencing-v2__role-segment sentencing-v2__role-segment--${role}`}
              style={{ width: `${(count / totalSentences) * 100}%` }}
              title={`${ROLE_LABELS[role]}: ${count} (${((count / totalSentences) * 100).toFixed(0)}%)`}
            />
          );
        })}
      </div>

      {/* Role legend */}
      <div className="sentencing-v2__legend">
        {ROLE_ORDER.map(role => {
          const count = data.roleDistribution[role] || 0;
          if (count === 0) return null;
          return (
            <span key={role} className="sentencing-v2__legend-item">
              <span className={`sentencing-v2__legend-dot sentencing-v2__role-segment--${role}`}
                    style={{ display: 'inline-block' }} />
              {ROLE_LABELS[role]} {count}
            </span>
          );
        })}
      </div>

      {/* Section tabs */}
      <div className="analyze-tool__options-grid" role="tablist" style={{ marginTop: 'var(--space-sm)' }}>
        {(['sentences', 'redundancy', 'gaps'] as const).map(tab => (
          <button
            key={tab}
            className={`analyze-option ${showSection === tab ? 'analyze-option--selected' : ''}`}
            onClick={() => setShowSection(tab)}
            role="tab"
            aria-selected={showSection === tab}
          >
            <span className="analyze-option__label">
              {tab === 'sentences' ? `Sentences (${data.totalSentences})`
                : tab === 'redundancy' ? `Redundant (${data.redundancyClusters.length})`
                : `Gaps (${data.coherenceGaps.length})`}
            </span>
          </button>
        ))}
      </div>

      {/* Sentences view */}
      {showSection === 'sentences' && (
        <div className="sentencing-v2__sentences">
          {data.sentences.map(s => (
            <React.Fragment key={s.id}>
              <div
                className={`sentencing-v2__sentence sentencing-v2__sentence--${s.role}`}
                onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(expandedId === s.id ? null : s.id);
                  }
                }}
                aria-expanded={expandedId === s.id}
                aria-label={`${ROLE_LABELS[s.role]}: ${s.text.slice(0, 60)}`}
              >
                <span className="sentencing-v2__sentence-role" style={{ color: `var(--role-${s.role})` }}>
                  {ROLE_LABELS[s.role]}
                </span>
                <span className="sentencing-v2__sentence-text">{s.text}</span>
                {s.flags.length > 0 && (
                  <span className="sentencing-v2__sentence-flags">
                    {s.flags.map(f => (
                      <span key={f} className={`sentencing-v2__flag sentencing-v2__flag--${f}`}>
                        {f}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              {expandedId === s.id && <SentenceDetailPanel sentence={s} />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Redundancy clusters */}
      {showSection === 'redundancy' && (
        <div className="sentencing-v2__clusters">
          {data.redundancyClusters.length === 0 && (
            <div className="sentencing-v2__gap">No redundancy clusters detected.</div>
          )}
          {data.redundancyClusters.map(cluster => {
            const memberTexts = cluster.sentences.map(sid =>
              data.sentences.find(s => s.id === sid)?.text || sid
            );
            return (
              <div key={cluster.id} className="sentencing-v2__cluster">
                <div className="sentencing-v2__cluster-header">
                  <span>{cluster.sentences.length} similar sentences (sim: {cluster.cosineSimilarity.toFixed(2)})</span>
                  <span className="sentencing-v2__cluster-action">{cluster.suggestedAction}</span>
                </div>
                {memberTexts.map((text, i) => (
                  <div key={i} className="sentencing-v2__sentence-text" style={{ padding: '2px 0' }}>
                    {cluster.sentences[i] === cluster.bestRepresentative ? <strong>{text}</strong> : text}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Coherence gaps */}
      {showSection === 'gaps' && (
        <div className="sentencing-v2__clusters">
          {data.coherenceGaps.length === 0 && (
            <div className="sentencing-v2__gap">No significant coherence gaps detected.</div>
          )}
          {data.coherenceGaps.map((gap, i) => {
            const before = data.sentences.find(s => s.id === gap.beforeSentenceId);
            const after = data.sentences.find(s => s.id === gap.afterSentenceId);
            return (
              <div key={i} className="sentencing-v2__gap">
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                  Gap magnitude: {gap.gapMagnitude.toFixed(3)}
                </div>
                {before && <div className="sentencing-v2__sentence-text">Before: {before.text}</div>}
                {after && <div className="sentencing-v2__sentence-text">After: {after.text}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div className="analyze-tool__meta">
        {data.processingTimeMs}ms | sentencing-v2 | {data.totalSentences} sentences
      </div>
    </div>
  );
}

function SentenceDetailPanel({ sentence: s }: { sentence: SentencingV2Sentence }) {
  const rs = s.readerState;
  const cs = s.constraintSignals;

  const allMarkers = [
    ...cs.commitmentMarkers.map(m => ({ text: m, type: 'commit' })),
    ...cs.hedgeMarkers.map(m => ({ text: m, type: 'hedge' })),
    ...cs.temporalMarkers.map(m => ({ text: m, type: 'temporal' })),
    ...cs.managerMarkers.map(m => ({ text: m, type: 'manager' })),
  ];

  return (
    <div className="sentencing-v2__detail">
      <div className="sentencing-v2__detail-grid">
        <div className="sentencing-v2__detail-section">
          <div className="sentencing-v2__detail-header">Reader State</div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Trace dist</span>
            <span className="sentencing-v2__detail-value">{rs.traceDistance.toFixed(3)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Purity</span>
            <span className="sentencing-v2__detail-value">{rs.purity.toFixed(3)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Entropy</span>
            <span className="sentencing-v2__detail-value">{rs.entropy.toFixed(3)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Dom. shift</span>
            <span className="sentencing-v2__detail-value">{rs.dominantShift.toFixed(3)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Coherence</span>
            <span className="sentencing-v2__detail-value">{rs.globalCoherence.toFixed(3)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Eff. rank</span>
            <span className="sentencing-v2__detail-value">{rs.effectiveRank}</span>
          </div>
        </div>
        <div className="sentencing-v2__detail-section">
          <div className="sentencing-v2__detail-header">Constraint Signals</div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">SIC</span>
            <span className="sentencing-v2__detail-value">{cs.sicScore.toFixed(2)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Irreversibility</span>
            <span className="sentencing-v2__detail-value">{cs.irreversibilityScore.toFixed(2)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Hedging</span>
            <span className="sentencing-v2__detail-value">{cs.hedgingScore.toFixed(2)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Temporal</span>
            <span className="sentencing-v2__detail-value">{cs.temporalPressure.toFixed(2)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Manager</span>
            <span className="sentencing-v2__detail-value">{cs.managerVoiceScore.toFixed(2)}</span>
          </div>
          <div className="sentencing-v2__detail-row">
            <span className="sentencing-v2__detail-label">Specificity</span>
            <span className="sentencing-v2__detail-value">{cs.specificityScore.toFixed(2)}</span>
          </div>
        </div>
      </div>
      {allMarkers.length > 0 && (
        <div className="sentencing-v2__markers">
          {allMarkers.map((m, i) => (
            <span key={i} className="sentencing-v2__marker" title={m.type}>
              {m.text}
            </span>
          ))}
        </div>
      )}
      <div className="sentencing-v2__detail-row" style={{ marginTop: 'var(--space-xs)' }}>
        <span className="sentencing-v2__detail-label">Confidence</span>
        <span className="sentencing-v2__detail-value">{(s.confidence * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function AnalyzeTool({ className = '', onSentencingComplete, onRoleClassifierComplete, onNoemaGraphComplete, onSicComplete, onChapterGraphComplete }: AnalyzeToolProps): React.ReactElement {
  const { workingContent, selectedText, focusedText } = useBufferSync();
  const analyzeApi = useAnalyzeApi();
  const { activeBook } = useBook();

  const [selectedAnalysis, setSelectedAnalysis] = useState<AnalysisOption | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [status, setStatus] = useState<AnalysisStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [sicProgress, setSicProgress] = useState<SicProgressUpdate | null>(null);
  const [noemaProgress, setNoemaProgress] = useState<NoemaProgressUpdate | null>(null);
  const abortRef = useRef(false);

  // GPTZero toggle
  const [gptzeroEnabled, setGptzeroEnabled] = useState(false);

  // Chapter graph progress
  const [chapterGraphProgress, setChapterGraphProgress] = useState<ChapterGraphProgress | null>(null);

  // Comparison text for noema-diff
  const [compareText, setCompareText] = useState('');
  const gptzeroAvail = analyzeApi.gptzeroAvailability;

  // Scope: current message vs all content
  const [useAllContent, setUseAllContent] = useState(false);

  const hasContent = workingContent.length > 0 || !!focusedText;
  const fullText = useMemo(
    () => workingContent.map((item) => item.text).join('\n\n'),
    [workingContent]
  );
  // Priority: selected text > focused message > full buffer (unless user chose "all")
  const defaultText = focusedText && !useAllContent ? focusedText : fullText;
  const contentText = selectedText || defaultText;

  const canAnalyze = hasContent && selectedAnalysis !== null;

  const handleSelectAnalysis = useCallback((option: AnalysisOption) => {
    setSelectedAnalysis(option);
    setResult(null);
    setError(null);
    // Clear noema graph data when switching analysis types
    if (option.id !== 'noema-graph') {
      onNoemaGraphComplete?.(null);
    }
    // Clear SIC data when switching away from SIC
    if (option.id !== 'sic') {
      onSicComplete?.(null);
    }
  }, [onNoemaGraphComplete, onSicComplete]);

  const handleRunAnalysis = useCallback(async () => {
    if (!canAnalyze || !selectedAnalysis) return;

    // Check availability
    if (!analyzeApi.isAvailable) {
      setError(analyzeApi.unavailableReason || 'Analysis not available');
      return;
    }

    // Check LLM availability for LLM-requiring analyses
    if (selectedAnalysis.needsLlm && !analyzeApi.hasLLMAnalysis) {
      setError('This analysis requires LLM. Start Ollama or sign in for cloud access.');
      return;
    }

    setStatus('running');
    setError(null);
    setResult(null);
    abortRef.current = false;

    try {
      switch (selectedAnalysis.id) {
        case 'ai-detection': {
          if (gptzeroEnabled && gptzeroAvail.available) {
            // Run both statistical + GPTZero in parallel
            const [statData, gptzeroData] = await Promise.all([
              analyzeApi.detect(contentText),
              analyzeApi.detectGptzero(contentText).catch((err: Error) => {
                console.warn('[AnalyzeTool] GPTZero failed:', err.message);
                setError(`GPTZero: ${err.message}`);
                return undefined;
              }),
            ]);
            setResult({ type: 'detection', data: statData, gptzeroData: gptzeroData || undefined });
          } else {
            const data = await analyzeApi.detect(contentText);
            setResult({ type: 'detection', data });
          }
          break;
        }
        case 'sic': {
          setSicProgress(null);
          const data = await analyzeApi.sic(contentText, undefined, (progress) => {
            setSicProgress(progress);
          });
          setSicProgress(null);
          setResult({ type: 'sic', data });
          onSicComplete?.(data);
          break;
        }
        case 'sentencing': {
          const session = await analyzeApi.sentenceStart(contentText);
          const steps: SentenceStepResult['step'][] = [];
          const total = session.totalSentences;

          setResult({
            type: 'sentencing',
            data: { steps, totalSentences: total, progress: 0 },
          });

          for (let i = 0; i < total; i++) {
            if (abortRef.current) break;
            const stepResult = await analyzeApi.sentenceStep(session.sessionId);
            if (stepResult.complete) break;
            steps.push(stepResult.step);
            setResult({
              type: 'sentencing',
              data: { steps: [...steps], totalSentences: total, progress: (i + 1) / total },
            });
          }
          // Signal completion with final progress
          setResult({
            type: 'sentencing',
            data: { steps: [...steps], totalSentences: total, progress: 1 },
          });
          onSentencingComplete?.(steps);
          break;
        }
        case 'sentencing-v2': {
          const v2Result = await analyzeApi.sentencingV2(contentText, {
            title: 'Workspace Analysis',
            mode: 'mixed',
          });
          setResult({ type: 'sentencing-v2', data: v2Result });
          onRoleClassifierComplete?.(v2Result);
          break;
        }
        case 'noema-graph': {
          setNoemaProgress(null);
          const graphResult = await analyzeApi.noemaGraph(contentText, {
            title: 'Workspace Analysis',
            mode: 'mixed',
            useSentencingHints: true,
          }, (progress) => {
            setNoemaProgress(progress);
          });
          setNoemaProgress(null);
          setResult({ type: 'noema-graph', data: graphResult });
          onNoemaGraphComplete?.(graphResult);
          break;
        }
        case 'noema-diff': {
          if (!compareText.trim()) {
            setError('Paste comparison text in the text box below, then run analysis.');
            break;
          }
          const diffResult = await analyzeApi.noemaGraphDiff(contentText, compareText, {
            title: 'Graph Diff',
            mode: 'mixed',
            useSentencingHints: false,
          });
          setResult({ type: 'noema-diff', data: diffResult });
          break;
        }
        case 'chapter-graph': {
          if (!activeBook) {
            setError('No active book. Open a book to use Chapter Graph analysis.');
            break;
          }
          const chaptersWithDrafts = activeBook.chapters
            .filter(ch => ch.draftBufferId)
            .map(ch => ({ index: ch.position, title: ch.title, draftBufferId: ch.draftBufferId! }));
          if (chaptersWithDrafts.length < 2) {
            setError(`Need at least 2 chapters with drafts. Currently: ${chaptersWithDrafts.length} chapter${chaptersWithDrafts.length === 1 ? ' has' : 's have'} a draft.`);
            break;
          }
          setChapterGraphProgress(null);
          const cgResult = await analyzeApi.chapterGraph(chaptersWithDrafts, (progress) => {
            setChapterGraphProgress(progress);
          });
          setChapterGraphProgress(null);
          setResult({ type: 'chapter-graph', data: cgResult });
          onChapterGraphComplete?.(cgResult);
          break;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setStatus('idle');
    }
  }, [canAnalyze, selectedAnalysis, contentText, analyzeApi, onSentencingComplete, onRoleClassifierComplete, onNoemaGraphComplete, onSicComplete, onChapterGraphComplete, gptzeroEnabled, gptzeroAvail, compareText, activeBook]);

  const handleCancel = useCallback(() => {
    abortRef.current = true;
    setStatus('idle');
    setError(null);
  }, []);

  const handleClearResults = useCallback(() => {
    setResult(null);
    setError(null);
    abortRef.current = true;
    onSentencingComplete?.([]); // Clear workspace overlays
    onRoleClassifierComplete?.(null); // Clear v2 overlays
    onNoemaGraphComplete?.(null); // Clear noema graph data
    onSicComplete?.(null); // Clear SIC data
    onChapterGraphComplete?.(null); // Clear chapter graph data
  }, [onSentencingComplete, onRoleClassifierComplete, onNoemaGraphComplete, onSicComplete, onChapterGraphComplete]);

  return (
    <div className={`analyze-tool ${className}`}>
      {/* Unavailability Banner */}
      {!analyzeApi.isAvailable && (
        <div className="analyze-tool__unavailable" role="alert">
          <span aria-hidden="true">{'\u26A0\uFE0F'}</span>
          <span>{analyzeApi.unavailableReason || 'Analysis unavailable'}</span>
          {analyzeApi.backend !== 'none' && (
            <span className="analyze-tool__backend-badge">{analyzeApi.backend}</span>
          )}
        </div>
      )}

      {/* Status bar */}
      <div className="analyze-tool__selection">
        {hasContent ? (
          <span className="analyze-tool__selection-count">
            {workingContent.length} item{workingContent.length !== 1 ? 's' : ''} in workspace
            ({contentText.split(/\s+/).filter(Boolean).length} words)
          </span>
        ) : (
          <span className="analyze-tool__selection-empty">
            No content to analyze
          </span>
        )}
      </div>

      {/* Source Indicator + Scope Toggle */}
      {hasContent && (
        <div className="analyze-tool__source-indicator">
          {selectedText
            ? `Selection (${selectedText.split(/\s+/).filter(Boolean).length} words)`
            : focusedText && !useAllContent
              ? `Current message (${focusedText.split(/\s+/).filter(Boolean).length} words)`
              : `Full buffer (${fullText.split(/\s+/).filter(Boolean).length} words)`}
          {focusedText && !selectedText && (
            <button
              className="analyze-tool__scope-toggle"
              onClick={() => setUseAllContent(v => !v)}
              title={useAllContent ? 'Analyze current message only' : 'Analyze all messages'}
            >
              {useAllContent ? '← Current msg' : 'All messages →'}
            </button>
          )}
        </div>
      )}

      {/* Analysis Options Grid */}
      <div className="analyze-tool__options">
        <div className="analyze-tool__options-header">Analysis Type</div>
        <div className="analyze-tool__options-grid" role="listbox" aria-label="Analysis options">
          {ANALYSIS_OPTIONS.map((option) => (
            <button
              key={option.id}
              className={`analyze-option ${selectedAnalysis?.id === option.id ? 'analyze-option--selected' : ''}`}
              onClick={() => handleSelectAnalysis(option)}
              disabled={!hasContent}
              role="option"
              aria-selected={selectedAnalysis?.id === option.id}
            >
              <span className="analyze-option__icon" aria-hidden="true">
                {option.icon}
              </span>
              <span className="analyze-option__label">{option.label}</span>
              {option.needsLlm && (
                <span className="analyze-option__badge">LLM</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Selected Analysis Description */}
      {selectedAnalysis && (
        <div className="analyze-tool__info">
          <p className="analyze-tool__description">{selectedAnalysis.description}</p>
        </div>
      )}

      {/* GPTZero Toggle (shown only when AI Detection is selected) */}
      {selectedAnalysis?.id === 'ai-detection' && (
        <div className="analyze-tool__gptzero-toggle">
          <label className="analyze-tool__toggle-label">
            <input
              type="checkbox"
              checked={gptzeroEnabled}
              onChange={(e) => {
                setGptzeroEnabled(e.target.checked);
                setError(null);
              }}
              disabled={!gptzeroAvail.available}
            />
            <span className="analyze-tool__toggle-text">
              GPTZero Neural Detection
              {gptzeroAvail.available && gptzeroAvail.source === 'byok' && (
                <span className="analyze-tool__toggle-badge analyze-tool__toggle-badge--byok">BYOK</span>
              )}
              {gptzeroAvail.available && gptzeroAvail.source === 'cloud' && (
                <span className="analyze-tool__toggle-badge analyze-tool__toggle-badge--cloud">
                  Cloud{gptzeroAvail.quotaUsed != null ? ` ${gptzeroAvail.quotaUsed}/${gptzeroAvail.quotaLimit}` : ''}
                </span>
              )}
            </span>
          </label>
          {!gptzeroAvail.available && (
            <span className="analyze-tool__toggle-hint">
              Add GPTZero key in Settings, or upgrade to Pro+
            </span>
          )}
        </div>
      )}

      {/* Comparison text for Graph Diff */}
      {selectedAnalysis?.id === 'noema-diff' && (
        <div className="analyze-tool__compare-input">
          <label className="analyze-tool__compare-label">
            Comparison Text (paste the text to compare against):
          </label>
          <textarea
            className="analyze-tool__compare-textarea"
            value={compareText}
            onChange={(e) => setCompareText(e.target.value)}
            placeholder="Paste the second text here to compare its intentional graph against the workspace content..."
            rows={4}
          />
          {compareText && (
            <span className="analyze-tool__compare-meta">
              {compareText.split(/\s+/).filter(Boolean).length} words
            </span>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="analyze-tool__error" role="alert">
          <span aria-hidden="true">&#x26A0;&#xFE0F;</span>
          {error}
        </div>
      )}

      {/* SIC Progress */}
      {status === 'running' && selectedAnalysis?.id === 'sic' && sicProgress && (
        <div className="analyze-tool__sic-progress">
          <div className="analyze-tool__progress-bar">
            <div
              className="analyze-tool__progress-fill"
              style={{ width: `${(sicProgress.llmCalls / sicProgress.totalExpectedCalls) * 100}%` }}
            />
          </div>
          <span className="analyze-tool__progress-label">
            {sicProgress.detail} ({sicProgress.llmCalls} LLM call{sicProgress.llmCalls !== 1 ? 's' : ''})
          </span>
        </div>
      )}

      {/* Noema Graph Progress */}
      {status === 'running' && selectedAnalysis?.id === 'noema-graph' && noemaProgress && (
        <div className="analyze-tool__sic-progress">
          <div className="analyze-tool__progress-bar">
            <div
              className="analyze-tool__progress-fill"
              style={{ width: `${noemaProgress.total > 0 ? (noemaProgress.current / noemaProgress.total) * 100 : 0}%` }}
            />
          </div>
          <span className="analyze-tool__progress-label">
            {noemaProgress.message}
          </span>
        </div>
      )}

      {/* Chapter Graph Progress */}
      {status === 'running' && selectedAnalysis?.id === 'chapter-graph' && chapterGraphProgress && (
        <div className="analyze-tool__sic-progress">
          <div className="analyze-tool__progress-bar">
            <div
              className="analyze-tool__progress-fill"
              style={{ width: `${chapterGraphProgress.totalChapters > 0 ? ((chapterGraphProgress.chapterIndex + 1) / chapterGraphProgress.totalChapters) * 100 : 0}%` }}
            />
          </div>
          <span className="analyze-tool__progress-label">
            {chapterGraphProgress.message}
          </span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="analyze-tool__results">
          <div className="analyze-tool__results-header">
            <span>Results</span>
            <button
              className="analyze-tool__results-close"
              onClick={handleClearResults}
              aria-label="Clear results"
            >
              ×
            </button>
          </div>
          <div className="analyze-tool__results-content">
            {result.type === 'detection' && result.gptzeroData && (
              <GPTZeroResultView data={result.gptzeroData} />
            )}
            {result.type === 'detection' && <DetectionResultView data={result.data} />}
            {result.type === 'sic' && <SicResultView data={result.data} />}
            {result.type === 'sentencing' && <SentencingResultView data={result.data} />}
            {result.type === 'sentencing-v2' && <SentencingV2ResultView data={result.data} />}
            {result.type === 'noema-graph' && <NoemaGraphView data={result.data} />}
            {result.type === 'noema-diff' && (
              <NoemaGraphDiffView
                sourceGraph={result.data.sourceGraph}
                targetGraph={result.data.targetGraph}
                diff={result.data}
                sourceLabel="Source"
                targetLabel="Target"
              />
            )}
            {result.type === 'chapter-graph' && <ChapterGraphView data={result.data} />}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="analyze-tool__actions">
        {status === 'running' ? (
          <button
            className="analyze-tool__btn analyze-tool__btn--cancel"
            onClick={handleCancel}
          >
            Cancel
          </button>
        ) : (
          <button
            className="analyze-tool__btn analyze-tool__btn--primary"
            onClick={handleRunAnalysis}
            disabled={!canAnalyze}
          >
            Run Analysis
          </button>
        )}
      </div>

      {/* Empty state */}
      {!hasContent && (
        <div className="analyze-tool__placeholder">
          <span aria-hidden="true">{'\u{1F52C}'}</span>
          <p>No content in workspace</p>
          <p className="analyze-tool__placeholder-hint">
            Use the archive browser or search to add content to the workspace
          </p>
        </div>
      )}
    </div>
  );
}

export default AnalyzeTool;
